import type { PersonProfileFields } from '@/lib/person-profile';
/**
 * Directory RTK Query feature.
 *
 * Wraps the `/api/v1/directory/*` endpoints - the People and Companies
 * directory views derived from existing leads. All endpoints are read-only:
 * the directory aggregates and dedupes entities that already live in lead
 * lists; rows are created/edited through the lists themselves.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

// ─── Response shapes ──────────────────────────────────────────────────────

/** LIST shape - used by /directory/people and /directory/companies/{id}/people.
 *
 *  Superset of the original summary plus most of the detail fields
 *  (scalars + simple string lists). Nested object arrays (experiences,
 *  education, lead_lists, custom_fields) stay DETAIL-only. */
export interface DirectoryPersonSummary extends PersonProfileFields {
  id: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  /** Lowercased email when the lead has one. `null` when the row is keyed
   *  by the bare lead UUID (a lead without an email yet, e.g. fresh
   *  LinkedIn imports pre-enrichment). */
  email: string | null;
  phone: string | null;
  linkedin_url: string | null;
  profile_pic_url: string | null;
  company_id: string | null;
  company_name: string | null;
  company_linkedin_url: string | null;
  company_domain: string | null;
  location: string | null;
  /** Routing for per-row enrichment from the People list. The canonical
   *  Lead.id + LeadList.id this row resolves to. */
  lead_id: string | null;
  lead_list_id: string | null;
  // ─── Detail-shaped extras ─────────────────────────────────────────────
  public_identifier: string | null;
  twitter_handle: string | null;
  summary: string | null;
  headline: string | null;
  background_cover_image_url: string | null;
  // Backend email-verification vocabulary. verified/likely_valid read as
  // "good", risky/invalid as "bad", unknown as neutral.
  primary_email_status:
    | 'verified'
    | 'likely_valid'
    | 'risky'
    | 'invalid'
    | 'unknown'
    | null;
  /** Empty on emailled rows (jsonb aggregation isn't supported). Hydrate
   *  via the detail endpoint if you need the full list. */
  other_emails: string[];
  personal_emails: string[];
  personal_phone_numbers: string[];
  phone_metadata?: Record<string, Record<string, unknown>> | null;
  city: string | null;
  state: string | null;
  country: string | null;
  current_company_logo_url: string | null;
  current_company_headcount: number | null;
  current_company_headcount_range: string | null;
  current_company_location: string | null;
  current_company_description: string | null;
  current_company_founded_year: number | null;
  current_company_industry: string | null;
  current_company_website: string | null;
  current_company_revenue: string | null;
  all_employers: string[];
  all_titles: string[];
  skills: string[];
  languages: string[];
  enriched_provider: string | null;
  enriched_at: string | null;
  origin: string | null;
  created_at: string | null;
  last_modified_at: string | null;
  crm_contact_id: string | null;
  crm_migrated_at: string | null;
  synced_to_crm_at: string | null;
  lead_count: number;
}

/** LIST shape - used by /directory/companies.
 *
 *  Superset of the original summary plus most of the detail fields
 *  (scalars + simple string lists). Nested object fields (`hq`,
 *  `locations`, `lead_lists`) stay DETAIL-only. */
export interface DirectoryCompanySummary {
  id: string;
  name: string | null;
  domain: string;
  website: string | null;
  industry: string | null;
  company_type: string | null;
  founded_year: number | null;
  employee_count_min: number | null;
  employee_count_max: number | null;
  headcount?: number | null;
  employee_count_on_linkedin: number | null;
  linkedin_url: string | null;
  logo_url: string | null;
  hq_location: string | null;
  hq_timezone?: string | null;
  last_update_minutes: number | null;
  lead_count: number;
  // ─── Detail-shaped extras ─────────────────────────────────────────────
  tagline: string | null;
  description: string | null;
  follower_count: number | null;
  headcount_range: string | null;
  revenue: string | null;
  linkedin_internal_id: string | null;
  universal_name_id: string | null;
  background_cover_image_url: string | null;
  is_stub: boolean;
  created_at: string | null;
  modified_at: string | null;
  industries: string[];
}

/** Reference to a lead list this entity appears in. */
export interface DirectoryLeadListRef {
  id: string;
  name: string;
}

export interface DirectoryCompanyLocation {
  state_code?: string | null;
  country_code?: string | null;
  formatted_address?: string | null;
  timezone?: string | null;
  country: string | null;
  city: string | null;
  postal_code: string | null;
  line_1: string | null;
  is_hq: boolean;
  state: string | null;
}

/** DETAIL shape - full payload for one company. Adds nested-object arrays
 *  on top of the LIST shape. */
export interface DirectoryCompanyDetail extends DirectoryCompanySummary {
  hq: DirectoryCompanyLocation | null;
  locations: DirectoryCompanyLocation[];
  lead_lists: DirectoryLeadListRef[];
}

/** Date stored as `{year, month, day}` on Crustdata-shaped JSONB. */
export interface DirectoryDatePart {
  year: number | null;
  month: number | null;
  day: number | null;
}

/** Work experience record from the enrichment provider. */
export interface DirectoryExperience {
  is_current?: boolean | null;
  is_default?: boolean | null;
  starts_at: DirectoryDatePart | null;
  ends_at: DirectoryDatePart | null;
  company: string | null;
  company_linkedin_profile_url: string | null;
  /** Real domain string when the provider supplies it directly (Crustdata).
   *  Use this in preference to LinkedIn-slug guesses for logo lookups. */
  company_website_domain: string | null;
  title: string | null;
  description: string | null;
  location: string | null;
  logo_url: string | null;
}

/** Education record from the enrichment provider. */
export interface DirectoryEducation {
  starts_at: DirectoryDatePart | null;
  ends_at: DirectoryDatePart | null;
  field_of_study: string | null;
  degree_name: string | null;
  alma_mater: string | null;
  alma_mater_linkedin_profile_url: string | null;
  description: string | null;
  logo_url: string | null;
  grade: string | null;
  activities_and_societies: string | null;
}

/** DETAIL shape - full payload for one person. Adds nested-object arrays
 *  + enrichment company URL on top of the LIST shape. */
export interface DirectoryPersonDetail extends DirectoryPersonSummary {
  current_company_linkedin_url: string | null;
  current_employers_raw: Record<string, unknown>[];
  experiences: DirectoryExperience[];
  education: DirectoryEducation[];
  lead_lists: DirectoryLeadListRef[];
  custom_fields: Record<string, unknown>;
}

// ─── API ──────────────────────────────────────────────────────────────────

/** Arg for the paged list endpoints. `limit`/`offset` opt into server-side
 *  pagination; omitting `limit` keeps the legacy single-response behavior
 *  (server-capped at 500). */
export interface DirectoryListArg {
  organizationId?: string;
  userId?: string;
  leadListId?: string;
  limit?: number;
  offset?: number;
  /** People only: order the whole directory by this summary field. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** People only: name, email, title or company contains this. */
  q?: string;
}

/** Object arguments partition reads without changing the resource URL. */
export type DirectoryRecordArg =
  | string
  | {
      id: string;
      organizationId?: string;
      userId?: string;
    };
const recordId = (arg: DirectoryRecordArg) =>
  typeof arg === 'string' ? arg : arg.id;
const scopedDirectoryRead = (
  url: string,
  arg: DirectoryListArg | DirectoryRecordArg | void
) => ({
  url,
  ...(typeof arg === 'object' && arg?.organizationId
    ? { headers: { 'X-Cleanlist-Expected-Org': arg.organizationId } }
    : {}),
});

/** One page of a directory list. `totalCount` is the caller-scoped server
 *  total from the `X-Total-Count` header — the backend sends it only on the
 *  first page (`offset === 0`) of a paginated request; `null` otherwise. */
export interface DirectoryPage<T> {
  items: T[];
  totalCount: number | null;
}

const directoryListUrl = (
  base: string,
  arg: DirectoryListArg | void
): string => {
  const params = new URLSearchParams();
  if (arg && typeof arg === 'object') {
    if (arg.leadListId) params.set('lead_list_id', arg.leadListId);
    if (arg.limit != null) params.set('limit', String(arg.limit));
    if (arg.offset) params.set('offset', String(arg.offset));
    if (arg.sortBy) {
      params.set('sort_by', arg.sortBy);
      params.set('sort_order', arg.sortOrder ?? 'asc');
    }
    if (arg.q) params.set('q', arg.q);
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
};

/** Read the server total from the `X-Total-Count` response header. The base
 *  query's meta is loosely typed, hence the narrow structural cast. */
const readTotalCountHeader = (meta: unknown): number | null => {
  const response = (meta as { response?: Response } | undefined)?.response;
  const raw = response?.headers?.get('x-total-count');
  if (raw == null) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

export const directoryApi = createApi({
  reducerPath: 'directoryApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['DirectoryPerson', 'DirectoryCompany'],
  endpoints: (builder) => ({
    // People (derived, read-only). Optionally scoped to a single lead list
    // and/or a limit/offset page (see DirectoryListArg).
    getDirectoryPeople: builder.query<
      DirectoryPage<DirectoryPersonSummary>,
      DirectoryListArg | void
    >({
      query: (arg) =>
        scopedDirectoryRead(directoryListUrl('/directory/people', arg), arg),
      transformResponse: (items: DirectoryPersonSummary[], meta) => ({
        items,
        totalCount: readTotalCountHeader(meta),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((p) => ({
                type: 'DirectoryPerson' as const,
                id: p.id,
              })),
              { type: 'DirectoryPerson', id: 'LIST' },
            ]
          : [{ type: 'DirectoryPerson', id: 'LIST' }],
    }),
    getDirectoryPerson: builder.query<
      DirectoryPersonDetail,
      DirectoryRecordArg
    >({
      query: (arg) =>
        scopedDirectoryRead(
          `/directory/people/${encodeURIComponent(recordId(arg))}`,
          arg
        ),
      providesTags: (_, __, personId) => [
        { type: 'DirectoryPerson', id: recordId(personId) },
      ],
    }),

    // Companies (derived, read-only). Optionally scoped to a single lead list
    // and/or a limit/offset page (see DirectoryListArg).
    getDirectoryCompanies: builder.query<
      DirectoryPage<DirectoryCompanySummary>,
      DirectoryListArg | void
    >({
      query: (arg) =>
        scopedDirectoryRead(directoryListUrl('/directory/companies', arg), arg),
      transformResponse: (items: DirectoryCompanySummary[], meta) => ({
        items,
        totalCount: readTotalCountHeader(meta),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((c) => ({
                type: 'DirectoryCompany' as const,
                id: c.id,
              })),
              { type: 'DirectoryCompany', id: 'LIST' },
            ]
          : [{ type: 'DirectoryCompany', id: 'LIST' }],
    }),
    getDirectoryCompany: builder.query<
      DirectoryCompanyDetail,
      DirectoryRecordArg
    >({
      query: (arg) =>
        scopedDirectoryRead(
          `/directory/companies/${encodeURIComponent(recordId(arg))}`,
          arg
        ),
      providesTags: (_, __, companyId) => [
        { type: 'DirectoryCompany', id: recordId(companyId) },
      ],
    }),

    // People at a single company. Companion to getDirectoryCompany; returns
    // LIST shape.
    getDirectoryCompanyPeople: builder.query<
      DirectoryPersonSummary[],
      DirectoryRecordArg
    >({
      query: (arg) =>
        scopedDirectoryRead(
          `/directory/companies/${encodeURIComponent(recordId(arg))}/people`,
          arg
        ),
      providesTags: (result, _err, companyId) =>
        result
          ? [
              ...result.map((p) => ({
                type: 'DirectoryPerson' as const,
                id: p.id,
              })),
              { type: 'DirectoryPerson', id: `COMPANY:${recordId(companyId)}` },
            ]
          : [{ type: 'DirectoryPerson', id: `COMPANY:${recordId(companyId)}` }],
    }),
  }),
});

export const {
  useGetDirectoryPeopleQuery,
  useLazyGetDirectoryPeopleQuery,
  useGetDirectoryPersonQuery,
  useGetDirectoryCompaniesQuery,
  useLazyGetDirectoryCompaniesQuery,
  useGetDirectoryCompanyQuery,
  useGetDirectoryCompanyPeopleQuery,
} = directoryApi;
